/**
 * Custom trade task wire schemas — project-scoped CRUD for the RAMS
 * "methods" surface (independent RAMS input prerequisite; this is INPUT
 * surface only, not RAMS generation activation).
 *
 * A project custom trade task (`ProjectCustomTradeTask`) carries its
 * trade/task/method INLINE so RAMS rendering is identical to the global
 * `TradeTask` library rows. A create saves EITHER explicit names + steps
 * OR snapshots the actor's own `AccountCustomTradeTask` TEMPLATE (union
 * request below). The two ownership models are load-bearing:
 *
 *   - Templates (`AccountCustomTradeTask`) are userId-OWNED — a snapshot
 *     request may only reference a template whose `userId` is the bearer
 *     actor. Snapping a team-mate's template is a 404.
 *   - Custom HAZARDS are accountId-owned (see ./hazards) — different owner,
 *     different route family, never conflated.
 *
 * A snapshot COPIES the template's fields into the project row and records
 * provenance in `accountCustomTradeTaskId`; later edits to the project copy
 * (PATCH) must NEVER write through to the template, and later edits to the
 * template must never rewrite the issued project copy.
 */
import { z } from "zod";
import { entityIdSchema, INT32_MAX, INT32_MIN, orderIndexSchema } from "../common";

/**
 * Field caps — wire-schema-only bounds on unbounded TEXT columns, matching
 * the custom-hazard caps' rationale (users paste multi-paragraph method
 * steps; the PDF renders whatever is stored). Names follow the 200-char
 * `CUSTOM_HAZARD_NAME_MAX` precedent.
 */
export const CUSTOM_TRADE_TASK_NAME_MAX = 200;
export const CUSTOM_TRADE_TASK_STEPS_MAX = 10_000;
export const CUSTOM_TRADE_TASK_NOTES_MAX = 10_000;

/** One project custom trade task (Prisma `ProjectCustomTradeTask`). */
export const ProjectCustomTradeTaskRecordSchema = z.object({
  id: z.string(),
  projectId: entityIdSchema,
  /**
   * Provenance: the actor-owned template this row was snapshotted from, or
   * `null` for an ad-hoc project-only task. Editing the row never follows
   * this link back to the template.
   */
  accountCustomTradeTaskId: z.string().nullable(),
  tradeName: z.string(),
  taskName: z.string(),
  methodSteps: z.string(),
  customNotes: z.string().nullable(),
  // STORED value, not an input cap (#2797 review): the record is an output
  // DTO over an unrestricted PostgreSQL Int — a PATCH to the accepted maximum
  // followed by an append legitimately stores 1,000,001, and validating that
  // with the input schema would turn a committed write into a 500. The
  // product cap stays on the create/update request schemas.
  orderIndex: z.number().int().min(INT32_MIN).max(INT32_MAX),
  createdAt: z.string().datetime(),
});
export type ProjectCustomTradeTaskRecord = z.infer<
  typeof ProjectCustomTradeTaskRecordSchema
>;

// #2411 server-boundary variant.
export const ProjectCustomTradeTaskRecordStrict =
  ProjectCustomTradeTaskRecordSchema.strict();

/**
 * POST /api/mobile/v1/projects/:projectId/custom-trade-tasks body — EITHER
 * snapshot an actor-owned template OR save explicit names + steps. Exactly
 * one shape must be present (a union, not optional fields, so a body with
 * both or neither is rejected rather than guessing).
 */
export const ProjectCustomTradeTaskCreateRequestSchema = z.union([
  z
    .object({
      accountCustomTradeTaskId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      tradeName: z
        .string()
        .min(1)
        .max(CUSTOM_TRADE_TASK_NAME_MAX),
      taskName: z
        .string()
        .min(1)
        .max(CUSTOM_TRADE_TASK_NAME_MAX),
      methodSteps: z
        .string()
        .min(1)
        .max(CUSTOM_TRADE_TASK_STEPS_MAX),
    })
    .strict(),
]);
export type ProjectCustomTradeTaskCreateRequest = z.infer<
  typeof ProjectCustomTradeTaskCreateRequestSchema
>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/custom-trade-tasks/:taskId body —
 * web parity (`app/api/projects/[projectId]/custom-trade-tasks/[taskId]`):
 * names, steps, notes and order are all editable on the project copy. This
 * NEVER accepts a template id — retargeting a project row to a different
 * template is not an edit, it is a new snapshot (create a new row).
 */
export const ProjectCustomTradeTaskUpdateRequestSchema = z
  .object({
    tradeName: z.string().min(1).max(CUSTOM_TRADE_TASK_NAME_MAX).optional(),
    taskName: z.string().min(1).max(CUSTOM_TRADE_TASK_NAME_MAX).optional(),
    methodSteps: z.string().min(1).max(CUSTOM_TRADE_TASK_STEPS_MAX).optional(),
    customNotes: z.string().max(CUSTOM_TRADE_TASK_NOTES_MAX).nullable().optional(),
    orderIndex: orderIndexSchema.optional(),
  })
  .strict();
export type ProjectCustomTradeTaskUpdateRequest = z.infer<
  typeof ProjectCustomTradeTaskUpdateRequestSchema
>;

/**
 * The actor's own reusable template, offered for snapshotting onto a project.
 * Read-only over the API (template CRUD is a web/account surface; the mobile
 * prerequisite only lists and snapshots).
 */
export const AccountCustomTradeTaskTemplateSchema = z.object({
  id: z.string(),
  tradeName: z.string(),
  taskName: z.string(),
  defaultMethodSteps: z.string(),
  createdAt: z.string().datetime(),
});
export type AccountCustomTradeTaskTemplate = z.infer<
  typeof AccountCustomTradeTaskTemplateSchema
>;

// #2411 server-boundary variant.
export const AccountCustomTradeTaskTemplateStrict =
  AccountCustomTradeTaskTemplateSchema.strict();
